// La pantalla dedicada del Agente (`/agente`): la MISMA conversación que el panel
// lateral, a ancho completo, más la columna de respuestas gráficas a la derecha.
//
// El encabezado toma los pills de "Asistente v2" —"Nuevo chat", "Historial de chats" y
// el "⋯", en el mismo gris de chrome— pero todos a la DERECHA, con el título "Agente"
// solo a la izquierda (pedido de Matías del 2026-09-12; v2 los tenía en las dos esquinas).
// Ver ADR-2026-09-12-el-agente-toma-el-diseno-del-asistente-v2.
//
// No hay un segundo motor ni un segundo estado: los dos leen el mismo
// `ChatAtlasProvider`, así que se puede empezar a escribir en el panel, abrir
// esta pantalla y seguir en la misma línea (mensajes, borrador, adjuntos
// enganchados e interruptores incluidos).
//
// ESTA ES LA ÚNICA PANTALLA QUE DIBUJA GRÁFICOS. En el panel lateral no hay
// ancho (~420px) y partirlo en dos sería ilegible, así que allá la tarjeta de un
// gráfico avisa y trae al usuario acá. Ver `PanelGraficosChat`.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { SquarePen } from 'lucide-react';
import { LienzoDelAgente } from '../lienzo/LienzoDelAgente';
import { useLienzoDelChat } from '../lienzo/useLienzoDelChat';
import { ANCHO_LOGO_SIDEBAR_MINIMA, useSidebar } from '../../context/SidebarContext';

/** Lo que tarda la sidebar en colapsarse (`EstiloMinimal`, `duration-300`), más un respiro. */
const RETARDO_SIDEBAR_MS = 320;
/** Lo que tarda la columna del lienzo en abrirse (su `transition`, `duration-300`). */
const RETARDO_COLUMNA_MS = 300;
import { useChatAtlas } from '../ChatAtlasContext';
import { BOTON_ESQUINA, BOTON_REDONDO_GRIS } from './estilosAgente';
import { anchoDeBiblioteca, type PestanaBiblioteca } from './bibliotecaDeAdjuntos';
import { PanelBiblioteca } from './PanelBiblioteca';
import { useExitTransition } from '../../hooks/useExitTransition';
import { graficoQueSeAbreSolo, ultimoGrafico } from '../graficosChat';
import { ConversacionChatAtlas } from './ConversacionChatAtlas';
import { MenuChat } from './MenuChat';
import { PanelGraficosChat } from './PanelGraficosChat';
import { PortadaDelAsistente } from './PortadaDelAsistente';
import { anchoColumnaConLienzo, vieneDelPanel } from './rutaChatAtlas';
import { useAccesoAlAgente } from './useAccesoAlAgente';
import { useEsEscritorio } from './useEsEscritorio';

export const PantallaChatAtlas: React.FC = () => {
  const chat = useChatAtlas();
  const esEscritorio = useEsEscritorio();
  // ESTA es la puerta de atrás que el piso de plan tenía abierta hasta
  // ADR-2026-09-14-el-agente-es-de-pro-y-ultra: `/agente` no la gateaba nadie y alcanzaba
  // con escribir la URL. Mientras el plan no resuelve NO expulsa —sacar a un Pro de la
  // pantalla mientras carga sería peor— pero tampoco dibuja el chat: espera
  // (ADR-2026-09-14-el-guard-del-agente-espera-la-respuesta-autoritativa).
  //
  // A planes y no a la home: es adónde manda esta app cualquier toque bloqueado, y quien
  // escribió `/agente` a mano está buscando el Agente, no el inicio. `replace` para que el
  // botón de volver no lo devuelva al rebote.
  const acceso = useAccesoAlAgente();

  // CÓMO SE LLEGÓ ACÁ CAMBIA LO QUE HAY QUE HACER (ver `EntradaAPantalla`):
  //  • expandiendo el panel → se conserva el contexto de apertura (el agente
  //    tiene que seguir sabiendo qué es "esto") y el panel queda abierto para
  //    cuando el usuario vuelva a otra pantalla;
  //  • por la sidebar o por la URL → no hay contexto ni panel previo, así que
  //    se limpia el uno y no se abre el otro.
  const { state: estadoNavegacion } = useLocation();
  const vengoDelPanel = vieneDelPanel(estadoNavegacion);

  // EN MOBILE la pantalla es la del Asistente de Crear, también arriba: sin la banda del
  // volver del cascarón (ni título ni "Nuevo chat" + "⋯" en ella, como hasta
  // ADR-2026-09-12-el-agente-en-mobile-usa-la-banda-del-volver). El fondo azul sube hasta
  // arriba y las pastillas flotan encima: volver con el logo, "Nuevo chat" con conversación
  // e Historial, como en Crear (ADR-2026-10-16-el-agente-se-ve-como-el-asistente-2). Las
  // dibuja `PortadaDelAsistente`; acá sólo se apaga el volver del shell.
  const {
    setMobileHideBack,
    setSidebarMinima,
    pedirColapsarSidebar,
    sidebarMinima: tiraDeSidebar,
    isCollapsed: sidebarColapsada,
  } = useSidebar();
  useEffect(() => {
    setMobileHideBack(true);
    return () => setMobileHideBack(false);
  }, [setMobileHideBack]);
  // LA BIBLIOTECA de adjuntos (ADR-2026-09-12-la-biblioteca-de-adjuntos-en-la-pantalla-
  // del-agente): la columna de la derecha desde la que se engancha de un toque lo que
  // el chat sabe adjuntar. Sólo en desktop. Comparte el lado derecho con la columna de
  // gráficos y NO conviven: abrir una cierra la otra (tres columnas no entran). Se abre
  // desde una card del "+", en la suya, y se cierra con su "X". El botón de biblioteca del
  // encabezado se fue (ADR-2026-10-08-la-biblioteca-sin-boton-en-el-encabezado).
  const [biblioteca, setBiblioteca] = useState<PestanaBiblioteca | null>(null);
  const ultimaPestanaRef = useRef<PestanaBiblioteca>('productos');
  const cerrarGrafico = chat.cerrarGrafico;
  const abrirBiblioteca = useCallback(
    (pestana: PestanaBiblioteca) => {
      ultimaPestanaRef.current = pestana;
      setBiblioteca(pestana);
      cerrarGrafico();
    },
    [cerrarGrafico],
  );
  const cambiarPestana = (pestana: PestanaBiblioteca) => {
    ultimaPestanaRef.current = pestana;
    setBiblioteca(pestana);
  };
  useEffect(() => {
    if (chat.graficoActivoId !== null) setBiblioteca(null);
  }, [chat.graficoActivoId]);
  // La apertura y el cierre EMPUJAN (ADR-2026-09-12-la-biblioteca-se-abre-y-se-cierra-
  // empujando): un contenedor con el ancho animado de cero a la medida de la columna,
  // y la columna adentro siempre a su medida final. Para eso el ancho va en píxeles,
  // calculado del ancho de la pantalla. `useExitTransition` mantiene la columna montada
  // mientras se cierra; en ese rato `biblioteca` ya es null, así que se dibuja con la
  // última pestaña vista.
  const contenedorRef = useRef<HTMLDivElement | null>(null);
  const [anchoContenedor, setAnchoContenedor] = useState(0);
  // `acceso` en las dependencias A PROPÓSITO: mientras el plan resuelve, la pantalla
  // devuelve `null` y el contenedor no existe. Con `[]` el efecto corría una sola vez,
  // con el ref vacío, y no medía nunca: el ancho quedaba en 0, la biblioteca clavada en
  // su piso (440) y la columna del chat con lienzo en el suyo (300). Cuando el acceso
  // se resuelve, el contenedor aparece y recién ahí hay algo que observar
  // (ADR-2026-09-16-con-el-lienzo-la-biblioteca-se-abre-encima-del-chat).
  useLayoutEffect(() => {
    const el = contenedorRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const medir = () => setAnchoContenedor(el.clientWidth);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, [acceso]);
  const anchoBiblioteca = anchoDeBiblioteca(anchoContenedor);
  const { mounted: bibliotecaMontada, animated: bibliotecaAbierta } = useExitTransition(
    biblioteca !== null,
    300,
  );

  // EL LIENZO (ADR-2026-09-16-el-lienzo-del-agente-es-react-flow): con al menos una
  // generación en el chat y sin que el usuario lo haya escondido, la conversación se
  // corre a la derecha con el ancho del panel lateral y el resto es el lienzo. Es la
  // vista del costado que ya existe, cambiada de lugar. Un gráfico abierto ocupa el
  // mismo lugar que el lienzo mientras dura, y la conversación vuelve a su ancho: así el
  // gráfico convive con la conversación exactamente como hoy.
  //
  // El hook corre UNA vez, acá, y el lienzo recibe el resultado por prop: el "oculto" y
  // las posiciones son un solo estado.
  const estadoLienzo = useLienzoDelChat();
  const hayLienzo = esEscritorio && estadoLienzo.tandas.length > 0;
  const lienzoVisible =
    hayLienzo && !estadoLienzo.lienzo.oculto && chat.graficoActivoId === null;
  // Un poco más ancha que la franja del panel, y es TAMBIÉN el ancho de la biblioteca en
  // este estado: los dos miden lo mismo y la biblioteca se abre encima del chat, tapándolo
  // justo, en vez de empujar al lienzo
  // (ADR-2026-09-16-con-el-lienzo-la-biblioteca-se-abre-encima-del-chat). La sidebar no se
  // resta porque el contenedor medido ya está adentro del panel de contenido.
  const anchoConversacion = anchoColumnaConLienzo(anchoContenedor);
  const anchoBibliotecaEfectivo = lienzoVisible ? anchoConversacion : anchoBiblioteca;
  // ENTRAR Y SALIR del modo lienzo se anima: el lienzo es un contenedor cuyo ancho va
  // de cero a lo que queda al lado de la conversación, y la conversación —siempre
  // `flex: 1`— se achica o se agranda con él. El mismo truco que la biblioteca
  // (ADR-2026-09-12-la-biblioteca-se-abre-y-se-cierra-empujando); `useExitTransition`
  // mantiene el lienzo montado mientras se cierra.
  // En el Agente la sidebar colapsada es una tira con sólo el logo, con o sin lienzo
  // (ADR-2026-09-16-el-lienzo-achica-la-sidebar-a-una-tira). Es una bandera sostenida,
  // como la del Estudio de Video: se apaga al irse del chat y el resto de la app vuelve
  // a ver la sidebar de siempre. Sólo en escritorio: en mobile no hay sidebar que achicar.
  useEffect(() => {
    setSidebarMinima(esEscritorio);
    return () => setSidebarMinima(false);
  }, [esEscritorio, setSidebarMinima]);
  // LA APERTURA DEL LIENZO ES UNA COREOGRAFÍA, no tres cosas a la vez
  // (ADR-2026-09-17-el-lienzo-abre-por-etapas). Cuando aparece: primero la sidebar se
  // colapsa (el lienzo es la pantalla de trabajo y cada pixel cuenta), después la
  // columna se abre desde cero, y recién con la columna abierta se muestra lo de adentro
  // —las cards de "Generando…" y el encuadre—. Superpuestas, las tres animaciones se
  // trababan entre sí y el lienzo se veía salir a los tirones con las cards apareciendo
  // encima. Si la sidebar ya estaba colapsada, esa etapa se saltea. Cerrar no espera
  // nada: la columna se va como siempre.
  type EtapaLienzo = 'cerrado' | 'sidebar' | 'columna' | 'contenido';
  const [etapaLienzo, setEtapaLienzo] = useState<EtapaLienzo>('cerrado');
  const sidebarColapsadaRef = useRef(sidebarColapsada);
  sidebarColapsadaRef.current = sidebarColapsada;
  useEffect(() => {
    if (!lienzoVisible) {
      setEtapaLienzo('cerrado');
      return;
    }
    // Se pide colapsar en el flanco: si el usuario la abre después, queda abierta.
    pedirColapsarSidebar();
    const esperaSidebar = sidebarColapsadaRef.current ? 0 : RETARDO_SIDEBAR_MS;
    setEtapaLienzo(esperaSidebar > 0 ? 'sidebar' : 'columna');
    const t1 = window.setTimeout(() => setEtapaLienzo('columna'), esperaSidebar);
    const t2 = window.setTimeout(() => setEtapaLienzo('contenido'), esperaSidebar + RETARDO_COLUMNA_MS);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [lienzoVisible, pedirColapsarSidebar]);
  const columnaAbierta = etapaLienzo === 'columna' || etapaLienzo === 'contenido';
  // La conversación cambia a la geometría de panel (ancho completo, sin columna
  // centrada) recién cuando la columna del lienzo TERMINÓ de abrirse. Cambiarla en el
  // mismo instante en que empieza —como estaba— la estiraba a todo el ancho de la
  // pantalla y después se la veía encogerse hacia el costado mientras el lienzo
  // entraba. Mientras la columna se abre conserva su columna centrada (`max-w-3xl`), que
  // ya es más angosta que la pantalla y sólo se achica cuando el contenedor la aprieta.
  // Al cerrar vuelve enseguida: la columna es angosta y el máximo no la toca.
  const conversacionEnPanel = etapaLienzo === 'contenido';

  const anchoLienzo = Math.max(0, anchoContenedor - anchoConversacion);
  const { mounted: lienzoMontado, animated: lienzoAbierto } = useExitTransition(
    columnaAbierta,
    RETARDO_COLUMNA_MS,
  );

  const entrarAPantalla = chat.entrarAPantalla;
  useEffect(() => {
    entrarAPantalla({ conservarContexto: vengoDelPanel });
    // Solo al montar: volver a correrlo en cada render pisaría el contexto de
    // apertura todo el tiempo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Un gráfico recién llegado se abre solo, pero SOLO en desktop: acá la
  // columna de la derecha aparece al lado de la conversación y se lee como
  // parte de la respuesta. En mobile el panel es una hoja a pantalla completa
  // y abrirla sola le taparía el chat al usuario en mitad de la lectura — ahí
  // se abre únicamente si toca la tarjeta.
  //
  // QUÉ gráfico lo decide `graficoQueSeAbreSolo`, que es puro y está testeado.
  // La cuenta no es "el más nuevo": si el usuario llegó desde el panel lateral
  // tocando la tarjeta de un gráfico viejo, ESE es el que quiere ver y abrirle
  // el último lo mandaría a cualquier lado.
  const ofrecidoRef = useRef<string | null | undefined>(undefined);
  const idMasNuevo = ultimoGrafico(chat.mensajes)?.id ?? null;
  // El gráfico abierto se lee por ref y no entra en las dependencias: es un
  // dato de entrada de la decisión, no un disparador. Si estuviera en las
  // deps, abrir uno volvería a correr el efecto en el acto.
  const abiertoRef = useRef<string | null>(chat.graficoActivoId);
  abiertoRef.current = chat.graficoActivoId;
  const abrirGrafico = chat.abrirGrafico;
  useEffect(() => {
    // En mobile no se registra NADA a propósito: así, si el usuario agranda la
    // ventana, la decisión se toma recién ahí, con su elección ya hecha.
    if (!esEscritorio) return;
    const aAbrir = graficoQueSeAbreSolo({
      idMasNuevo,
      idAbierto: abiertoRef.current,
      idYaOfrecido: ofrecidoRef.current,
    });
    ofrecidoRef.current = idMasNuevo;
    if (aAbrir) abrirGrafico(aAbrir);
  }, [idMasNuevo, esEscritorio, abrirGrafico]);

  // Después de TODOS los hooks: el rebote no puede saltearlos o React se queja del orden
  // en el render siguiente.
  // `cargando`: el cascarón ya está dibujado alrededor, así que esto es un momento sin
  // contenido, no una pantalla en blanco — y dura lo que tarda la consulta del plan.
  if (acceso === 'cargando') return null;
  if (acceso === 'denegado') return <Navigate to="/subscription" replace />;

  return (
    // El alto disponible NO es 100dvh en mobile: `DashboardLayout` le mete al
    // `<main>` un padding-top de `env(safe-area-inset-top) + 3.5rem + 0.75rem`
    // para el header flotante, y en desktop lo apaga (`md:pt-0`). Restarlo acá
    // es lo que deja la barra de entrada pegada abajo sin que la pantalla
    // scrollee de más. En desktop el panel de contenido es `fixed` de top-0 a
    // bottom-0 (ADR-171), así que 100dvh es exactamente el alto disponible.
    <div
      ref={contenedorRef}
      // En el teléfono sube sobre el lugar del header (sin el volver queda vacío), como
      // Crear: el fondo llega hasta arriba en vez de dejar una franja del color del shell.
      // Y baja sobre el de la barra inferior, que acá no se muestra pero el `main` reservaba
      // (ADR-2026-10-21-la-caja-del-asistente-en-el-telefono).
      className="flex h-[calc(100dvh-env(safe-area-inset-top))] overflow-hidden max-md:-mb-[var(--dashboard-bottom-offset)] max-md:-mt-[4.25rem] md:h-[100dvh]"
    >
      {lienzoMontado && (
        <div
          className="shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-out"
          style={{ width: lienzoAbierto ? anchoLienzo : 0, opacity: lienzoAbierto ? 1 : 0 }}
          aria-hidden={!lienzoAbierto}
        >
          <LienzoDelAgente
            estado={estadoLienzo}
            ancho={anchoLienzo}
            contenidoVisible={etapaLienzo === 'contenido'}
          />
        </div>
      )}
      {/* `min-w-0`: sin esto la columna de la conversación se niega a
          achicarse cuando aparece el panel de la derecha (contenido flex con
          texto largo) y termina desbordando. Siempre `flex: 1`: con el lienzo
          abierto le queda exactamente `anchoConversacion`, y mientras el lienzo se
          abre o se cierra acompaña su animación.

          EL ENCABEZADO ES DE ESTA COLUMNA, no de la pantalla: sus botones son los del
          chat y la biblioteca (y el gráfico) ocupan hasta arriba, a su lado
          (pedido de Matías del 2026-09-16). Con el lienzo a la vista la columna es
          angosta, así que Historial deja de ser pill y se achica a un ícono al lado de
          "Nuevo chat" (ADR-2026-10-08-historial-como-icono-con-el-lienzo); en el panel
          lateral, más angosto todavía, vive en el "⋯".

          Y con el lienzo toma también la ESTÉTICA del panel lateral (`PanelChatAtlas`):
          fondo `surface-content`, línea de 1px a la izquierda que la separa del lienzo y
          otra bajo el encabezado, como un panel dividido. Sin lienzo no hay nada que
          dividir y la columna se apoya en el fondo de la página, como siempre
          (ADR-2026-09-16-con-el-lienzo-la-conversacion-se-ve-como-el-panel). */}
      {/* SIN EL LIENZO A LA VISTA, la pantalla es la del Asistente de Crear: fondo azul, la
          demo, la caja de Crear y la conversación en el medio
          (ADR-2026-10-16-el-agente-se-ve-como-el-asistente). Con el lienzo, la columna de
          siempre al costado, que ya se ve como el panel del Asistente. */}
      {!lienzoVisible ? (
        <PortadaDelAsistente
          vistaGrafico="panel-derecho"
          onAbrirGrafico={chat.abrirGrafico}
          onVolverAlLienzo={
            hayLienzo && estadoLienzo.lienzo.oculto && chat.graficoActivoId === null
              ? () => estadoLienzo.setOculto(false)
              : undefined
          }
          margenIzquierdo={tiraDeSidebar ? ANCHO_LOGO_SIDEBAR_MINIMA : 0}
        />
      ) : (
      <div
        className={`relative flex min-w-0 flex-1 flex-col overflow-hidden ${
          lienzoVisible ? 'border-l border-[rgb(var(--brand-black-rgb)_/_0.07)] bg-surface-content' : ''
        }`}
      >
        <header
          // Sin lienzo, la columna arranca en el borde donde flota el logo de la sidebar
          // en tira: el encabezado le deja el lugar (el shell publica `sidebarMinima`
          // sólo cuando la tira está de verdad a la vista).
          style={!lienzoVisible && tiraDeSidebar ? { paddingLeft: ANCHO_LOGO_SIDEBAR_MINIMA } : undefined}
          className={`hidden h-16 shrink-0 items-center justify-between gap-3 transition-[padding] duration-300 ease-out md:flex ${
            lienzoVisible ? 'border-b border-[rgb(var(--brand-black-rgb)_/_0.06)] px-4' : 'px-4 sm:px-6'
          }`}
        >
          {/* La etiqueta del contexto también se muestra acá (y no sólo en el
              header del panel): al expandir una conversación abierta desde
              una pantalla, el usuario tiene que seguir viendo de qué estamos
              hablando. */}
          <h1 className="min-w-0 truncate text-[15px] font-semibold text-brand-black">
            {chat.contexto?.titulo ?? 'Asistente'}
            {!chat.contexto?.titulo && chat.contexto?.pantalla && (
              <span className="ml-2 text-[13px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.45)]">
                {chat.contexto.pantalla}
              </span>
            )}
          </h1>
          {/* Las acciones, todas a la derecha: Nuevo chat · Historial (pill; ícono con el
              lienzo) · "⋯" (Memoria, Skills, Consumo, Permisos) · Lienzo
              (sólo si hay generaciones; al final, con el mismo ícono de panel que usa la
              sidebar para colapsarse, espejado porque el lienzo abre a la derecha —
              ADR-2026-09-22-el-boton-del-lienzo-es-un-panel-a-la-derecha). */}
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={chat.reiniciar} className={BOTON_ESQUINA}>
              <SquarePen className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
              Nuevo chat
            </button>
            <MenuChat mostrarHistorial={lienzoVisible ? 'icono' : true} estilo="gris" />
            {hayLienzo && (
              <button
                type="button"
                onClick={() => estadoLienzo.setOculto(!estadoLienzo.lienzo.oculto)}
                aria-label={estadoLienzo.lienzo.oculto ? 'Mostrar el lienzo' : 'Ocultar el lienzo'}
                aria-pressed={!estadoLienzo.lienzo.oculto}
                title={estadoLienzo.lienzo.oculto ? 'Mostrar el lienzo' : 'Ocultar el lienzo'}
                className={`${BOTON_REDONDO_GRIS} ${
                  !estadoLienzo.lienzo.oculto ? 'bg-neutral-200' : ''
                }`}
              >
                {/* `right_panel_close` con el lienzo a la vista (el botón lo cierra),
                    `right_panel_open` escondido: la misma pareja que la sidebar
                    (`left_panel_*`), del lado en que abre el lienzo. */}
                <span className="material-symbols-rounded text-[18px] leading-none" aria-hidden="true">
                  {estadoLienzo.lienzo.oculto ? 'right_panel_open' : 'right_panel_close'}
                </span>
              </button>
            )}
          </div>
        </header>

        <ConversacionChatAtlas
          variante={conversacionEnPanel ? 'panel' : 'pantalla'}
          // Con el lienzo, la MISMA geometría que el panel lateral (`PanelChatAtlas`):
          // ancho completo y `px-4`. Es también la geometría de la caja espejo de la
          // biblioteca, que tiene que calcar a esta caja al pixel.
          anchoColumna={conversacionEnPanel ? 'max-w-full' : undefined}
          padding={conversacionEnPanel ? 'px-4' : undefined}
          vistaGrafico="panel-derecho"
          onAbrirGrafico={chat.abrirGrafico}
          onAbrirBiblioteca={esEscritorio ? abrirBiblioteca : undefined}
          // La caja con Producto, Modelo y Referencia, como el panel del Asistente.
          conTarjetas
        />

        {/* CON EL LIENZO, la biblioteca se abre ENCIMA de esta columna (mide lo mismo,
            así la tapa justo) y entra deslizándose desde la derecha: empujar al lienzo
            sería robarle ancho a lo que el usuario está mirando
            (ADR-2026-09-16-con-el-lienzo-la-biblioteca-se-abre-encima-del-chat). */}
        {lienzoVisible && bibliotecaMontada && (
          <div
            className="absolute inset-y-0 right-0 z-10 transition-[transform,opacity] duration-300 ease-out"
            style={{
              width: anchoBibliotecaEfectivo,
              transform: bibliotecaAbierta ? 'none' : 'translateX(24px)',
              opacity: bibliotecaAbierta ? 1 : 0,
            }}
            aria-hidden={!bibliotecaAbierta}
          >
            <PanelBiblioteca
              pestana={biblioteca ?? ultimaPestanaRef.current}
              ancho={anchoBibliotecaEfectivo}
              onCambiarPestana={cambiarPestana}
              onCerrar={() => setBiblioteca(null)}
              espejoDeAdjuntos
            />
          </div>
        )}
      </div>
      )}

      <PanelGraficosChat />
      {/* SIN LIENZO, la biblioteca es una columna hermana que empuja a la conversación
          (ADR-2026-09-12-la-biblioteca-se-abre-y-se-cierra-empujando). */}
      {esEscritorio && !lienzoVisible && bibliotecaMontada && (
        <div
          className="shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-out"
          style={{
            width: bibliotecaAbierta ? anchoBiblioteca : 0,
            opacity: bibliotecaAbierta ? 1 : 0,
          }}
          aria-hidden={!bibliotecaAbierta}
        >
          <PanelBiblioteca
            pestana={biblioteca ?? ultimaPestanaRef.current}
            ancho={anchoBiblioteca}
            onCambiarPestana={cambiarPestana}
            onCerrar={() => setBiblioteca(null)}
          />
        </div>
      )}
    </div>
  );
};

export default PantallaChatAtlas;
